// START IMPORTS
import {
  AppConstants,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { Stack, Text, Divider, Pagination } from '@ltvco/refresh-lib/theme';
import {
  isUserInPhoneOnlyTest,
  pluralizeString,
} from '@ltvco/refresh-lib/utils';
import {
  EmailVerificationModal,
  useAccount,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  useRecentReports,
  stringifyParams,
  generateQueryConfig,
  ReportList,
  type ReportType,
  type ReportFilter,
  DeleteAllButton,
  useReportDeleteAll,
  DeleteModal,
  type RecentReportSerialized,
} from '@ltvco/refresh-lib/v2';
import { HistoryRounded } from '@mui/icons-material';
import { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { useQueryParams } from 'use-query-params';

import { constants } from 'appConstants';
import { DEEPSEARCH_REPORT_TYPES_MAP } from 'components/ChatBV/constants';

import {
  LoadingReports,
  NullState,
  SearchAndFilterControls,
  RecentReportsCardDescription,
} from './components';
import { hasRunReports } from './utils';
// END IMPORTS

// START CONSTANTS
const queryConfig = generateQueryConfig();
const phoneAndFraudScanFilterOptions = [
  'reverse_phone_report',
  'phone_fraud_report',
  'email_fraud_report',
  'ip_fraud_report',
  'url_fraud_report',
] as const;
// END CONSTANTS

// START INTERFACES_AND_TYPES
interface RecentReportsProps {
  isInChatPage?: boolean;
  triggerReportLibraryClick?: (type: string, permalink: string) => void;
}
// END INTERFACES_AND_TYPES

export const RecentReports: React.FC<RecentReportsProps> = ({
  isInChatPage,
  triggerReportLibraryClick,
}) => {
  // START HOOKS_AND_QUERIES
  const { data: account } = useAccount(true);
  const {
    config: { reportTypeFilterOptions },
  } = useContext(AppConstants);
  const { session } = useSession();
  const [queryParams, setQueryParams] = useQueryParams(queryConfig);
  const { vinVariation, phoneOnlyVariation } =
    useSubscriptionAccessConfigContext();
  const { sort_by, sort_direction } = queryParams;

  const { phoneOnlyPlanNames } = constants;
  const isPhoneOnlyVariation =
    isUserInPhoneOnlyTest(account, phoneOnlyPlanNames) || phoneOnlyVariation;

  const baseReportTypes = (queryParams.report_type || []).filter(
    (type) => (type as ReportFilter) !== 'monitored_report'
  );
  const effectiveReportTypes =
    isPhoneOnlyVariation && baseReportTypes.length === 0
      ? [...phoneAndFraudScanFilterOptions]
      : baseReportTypes;
  const paramsForApi = { ...queryParams, report_type: effectiveReportTypes };
  const filteredParams = stringifyParams(paramsForApi);

  const { data: queryData, isLoading } = useRecentReports(filteredParams);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [filterTypes, setFilterTypes] = useState<ReportFilter[]>(
    queryParams.report_type || []
  );
  const [reportVerificationRequired, setReportVerificationRequired] =
    useState(false);
  const [deleteAllReportsModalOpen, setDeleteAllReportsModalOpen] =
    useState(false);
  const [currentReports, setCurrentReports] = useState(
    (queryData?.reports as RecentReportSerialized[]) || []
  );
  // END STATE_MANAGEMENT

  // START HOOKS_AND_QUERIES_DEPENDENT_ON_STATE
  const deleteAllMutation = useReportDeleteAll(setCurrentReports);
  // END HOOKS_AND_QUERIES_DEPENDENT_ON_STATE

  // START DERIVED_DATA_AND_UTILS
  const hideRecentReports =
    account?.account.user_info.hide_recent_reports || false;
  const reportTypes: ReportType[] = filterTypes.filter(
    (type) => type !== 'monitored_report'
  );
  const effectiveReportTypesForSync =
    isPhoneOnlyVariation && reportTypes.length === 0
      ? [...phoneAndFraudScanFilterOptions]
      : reportTypes;

  const totalReportsRef = useRef<number | null>(null);
  const totalReports = useMemo(() => {
    if (isLoading && totalReportsRef.current !== null) {
      return totalReportsRef.current;
    }
    return queryData?.meta?.report_quantities?.total_reports_count || 0;
  }, [queryData, isLoading]);

  const totalFilteredReports =
    queryData?.meta?.report_quantities?.filtered_reports_count || 0;
  const pageCount = Math.ceil(totalFilteredReports / 20);

  const reports = (queryData?.reports as RecentReportSerialized[]) || [];

  const hasZeroReports = totalReports === 0;
  const availableFilterTypeOptions = { ...reportTypeFilterOptions };

  const currentDeepsearchReportTypes = Object.values(
    DEEPSEARCH_REPORT_TYPES_MAP
  );

  const filteredReportTypeOptions = isInChatPage
    ? Object.keys(availableFilterTypeOptions)
        .filter((key) =>
          currentDeepsearchReportTypes.includes(key as ReportType)
        )
        .reduce((acc, key) => {
          acc[key] = availableFilterTypeOptions[key];
          return acc;
        }, {} as typeof availableFilterTypeOptions)
    : availableFilterTypeOptions;

  const getReportTypeFilterOptions = () => {
    if (vinVariation && filteredReportTypeOptions.vehicle_report) {
      return { vehicle_report: filteredReportTypeOptions.vehicle_report };
    } else if (isPhoneOnlyVariation) {
      return Object.keys(filteredReportTypeOptions)
        .filter((key) =>
          (phoneAndFraudScanFilterOptions as readonly string[]).includes(key)
        )
        .reduce((acc, key) => {
          acc[key] = filteredReportTypeOptions[key];
          return acc;
        }, {} as typeof filteredReportTypeOptions);
    }
    return filteredReportTypeOptions;
  };
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (!isLoading) {
      totalReportsRef.current = totalReports;
    }
  }, [totalReports, isLoading]);

  useEffect(() => {
    setReportVerificationRequired(hideRecentReports);
  }, [hideRecentReports]);

  useEffect(() => {
    if (isPhoneOnlyVariation && filterTypes.length === 0) {
      setFilterTypes([...phoneAndFraudScanFilterOptions] as ReportFilter[]);
    }
  }, [isPhoneOnlyVariation, filterTypes.length]);

  useEffect(() => {
    setQueryParams({
      report_type: effectiveReportTypesForSync,
      page: 1,
    });
  }, [
    setQueryParams,
    filterTypes,
    hasZeroReports,
    queryParams.search_by,
    queryParams.sort_by,
    queryParams.sort_direction,
    isPhoneOnlyVariation,
  ]);

  useEffect(() => {
    setCurrentReports(reports);
  }, [reports]);

  useEffect(() => {
    document.title = 'Recent Reports - BeenVerified';
  }, []);
  // END EFFECTS

  // START EVENT_HANDLERS
  const deleteAll = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    deleteAllMutation.mutate();
    setDeleteAllReportsModalOpen(false);
  };

  const handleGoBack = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setDeleteAllReportsModalOpen(false);
  };

  const deleteCurrentReport = (permalink: string) =>
    setCurrentReports(
      currentReports.filter(
        (report: RecentReportSerialized) => report.id !== permalink
      )
    );

  const handlePageChange = (page: number) => {
    setQueryParams({ page });
    window.scrollTo(0, 0);
  };
  // END EVENT_HANDLERS

  // START EARLY_RETURNS
  if (!isLoading && reports.length === 0 && !hasRunReports({ session })) {
    return (
      <Stack spacing={3.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          {isInChatPage ? (
            <Text variant="h3">Reports</Text>
          ) : (
            <>
              <HistoryRounded sx={{ fontSize: '1.25rem' }} />
              <Text variant="h3">Recent Reports</Text>
            </>
          )}
        </Stack>
        <NullState />
      </Stack>
    );
  }
  // END EARLY_RETURNS

  // START RENDER
  return (
    <>
      <Stack
        spacing={3.5}
        padding={2.5}
        paddingTop={1}
        width={isInChatPage ? '100%' : 'auto'}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {isInChatPage ? (
            <Text variant="h3">Reports</Text>
          ) : (
            <>
              <HistoryRounded sx={{ fontSize: '1.25rem' }} />
              <Text variant="h3">Recent Reports</Text>
            </>
          )}
        </Stack>
        <SearchAndFilterControls
          filterTypes={filterTypes}
          setFilterTypes={(filterTypes) => {
            setFilterTypes(filterTypes);
          }}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          reportTypeFilterOptions={getReportTypeFilterOptions()}
          sort_by={sort_by}
          sort_direction={sort_direction}
          isInChatPage={isInChatPage}
        />
        <Stack spacing={1.5} direction="row">
          <Text variant="caption">
            {currentReports.length}{' '}
            {pluralizeString('report', currentReports.length)} viewed
          </Text>
          <Divider
            sx={{ borderColor: 'text.primary' }}
            orientation="vertical"
            flexItem
          />
          <DeleteAllButton
            setDeleteAllReportsModalOpen={setDeleteAllReportsModalOpen}
          />
        </Stack>
        {isLoading ? (
          <LoadingReports />
        ) : (
          <ReportList
            reportList={currentReports}
            deleteCurrentReport={deleteCurrentReport}
            RecentReportsCardDescription={RecentReportsCardDescription}
            triggerDeepSearchClick={(
              reportType: string,
              reportPermaLink: string
            ) => {
              triggerReportLibraryClick?.(reportType, reportPermaLink);
            }}
          />
        )}
        {!isLoading && pageCount > 1 && (
          <Stack
            spacing={2}
            direction={'row'}
            sx={{ justifyContent: 'center' }}
          >
            <Pagination
              page={queryParams.page}
              count={pageCount}
              onChange={(_: any, page: number) => handlePageChange(page)}
              variant="outlined"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-page, & .MuiPaginationItem-ellipsis': {
                  display: 'none',
                },
              }}
            />
          </Stack>
        )}
        <>
          <DeleteModal
            isOpen={deleteAllReportsModalOpen}
            setIsOpen={handleGoBack}
            deleteMutation={deleteAll}
            title={'Delete all reports confirmation'}
            confirmationText="Are you sure you want to delete ALL of your reports?"
            deleteText="Delete all"
          />
        </>
      </Stack>
      <EmailVerificationModal
        isOpen={reportVerificationRequired}
        onClose={() => setReportVerificationRequired(false)}
      />
    </>
  );
  // END RENDER
};
